/**
 * W1-11a (app-registry-keymap): `frameScheduler: FrameScheduler` (contract render/types.ts, ARCHITECTURE.md §7.8).
 *
 * One requestAnimationFrame loop for the whole app. Views register with a priority and run in the order
 * transport -> preview -> timeline -> overlay -> idle (registration order within a priority):
 *  - paused: render on demand; `request(id)` marks a view dirty and it runs once on the next frame (a view is dirty
 *    when added, so it draws once); requests made during a frame run on the following frame;
 *  - playing: `setContinuous(true)` runs every non-idle view every frame; idle views still run only on request;
 *  - idle views run only while the frame is within its time budget, else they wait for the next frame;
 *  - hidden tab: the loop stops; when the tab is visible again every view is redrawn once.
 * `dtMs` is the time since the previous frame of a running loop (0 on the first frame after the loop was idle or
 * hidden). A throwing view is reported and the others still run. The loop never runs when nothing needs drawing.
 */

import type { Unsubscribe } from '../../core/util/types';
import type { FramePriority, FrameScheduler } from '../types';
import { installPerfOverlay, perfOverlayRequested } from './perfOverlay';

export const FRAME_PRIORITIES: readonly FramePriority[] = ['transport', 'preview', 'timeline', 'overlay', 'idle'];

/** Time a frame may spend before idle-priority views are deferred to the next frame. */
export const IDLE_BUDGET_MS = 10;

interface View {
  readonly id: string;
  readonly priority: FramePriority;
  readonly rank: number;
  readonly seq: number;
  readonly cb: (now: number, dtMs: number) => void;
}

/** Opaque handle of a scheduled frame (rAF id or timer). */
export type FrameHandle = unknown;

export interface FrameSchedulerDeps {
  readonly requestFrame?: (cb: (now: number) => void) => FrameHandle;
  readonly cancelFrame?: (handle: FrameHandle) => void;
  readonly now?: () => number;
  /** Document for visibility (null: always visible). */
  readonly document?: Pick<Document, 'visibilityState' | 'addEventListener' | 'removeEventListener'> | null;
  readonly idleBudgetMs?: number;
  readonly onError?: (id: string, err: unknown) => void;
}

/** Per-view frame timings (dev performance overlay, W3-03); averages are exponential (about the last 30 frames). */
export interface FrameProfile {
  /** Frames per second over the recent frames (0 when idle). */
  readonly fps: number;
  /** Average time spent in the views of a frame (ms). */
  readonly frameMs: number;
  /** Slowest frame since the last `profile()` call (ms). */
  readonly worstMs: number;
  readonly views: ReadonlyArray<{ readonly id: string; readonly priority: FramePriority; readonly ms: number }>;
}

export interface AppFrameScheduler extends FrameScheduler {
  readonly continuous: boolean;
  /** Times every view callback (off by default; the dev performance overlay turns it on). */
  setProfiling(on: boolean): void;
  readonly profiling: boolean;
  profile(): FrameProfile;
  /** True while a frame is scheduled. */
  readonly scheduled: boolean;
  /** Ids registered (diagnostics). */
  ids(): readonly string[];
  /** Mark every view dirty (theme change, resize of the whole window). */
  requestAll(): void;
  dispose(): void;
}

function defaultRequestFrame(): (cb: (now: number) => void) => FrameHandle {
  if (typeof requestAnimationFrame === 'function') return (cb) => requestAnimationFrame(cb);
  return (cb) =>
    setTimeout(() => {
      cb(typeof performance === 'undefined' ? Date.now() : performance.now());
    }, 16);
}

function defaultCancelFrame(): (handle: FrameHandle) => void {
  if (typeof cancelAnimationFrame === 'function') {
    return (h) => {
      if (typeof h === 'number') cancelAnimationFrame(h);
    };
  }
  return (h) => {
    clearTimeout(h as ReturnType<typeof setTimeout>);
  };
}

export function createFrameScheduler(deps: FrameSchedulerDeps = {}): AppFrameScheduler {
  const requestFrame = deps.requestFrame ?? defaultRequestFrame();
  const cancelFrame = deps.cancelFrame ?? defaultCancelFrame();
  const now = deps.now ?? (() => (typeof performance === 'undefined' ? Date.now() : performance.now()));
  const doc = deps.document === undefined ? (typeof document === 'undefined' ? null : document) : deps.document;
  const budget = deps.idleBudgetMs ?? IDLE_BUDGET_MS;
  const onError =
    deps.onError ??
    ((id: string, err: unknown) => {
      console.error(`frame scheduler: view "${id}" failed`, err);
    });

  const views = new Map<string, View>();
  let ordered: View[] | null = null;
  const dirty = new Set<string>();
  let continuous = false;
  /** The scheduled frame, if any. */
  let handle: { readonly id: FrameHandle } | null = null;
  let lastFrame: number | null = null;
  let inFrame = false;
  let seq = 0;
  // Profiling (dev overlay): exponential averages per view, frame total, frame intervals.
  let profiling = false;
  const viewMs = new Map<string, number>();
  let frameMs = 0;
  let worstMs = 0;
  let intervalMs = 0;
  let lastProfiled: number | null = null;
  const EMA = 1 / 30;
  const ema = (prev: number, x: number): number => (prev === 0 ? x : prev + (x - prev) * EMA);

  const hidden = (): boolean => doc?.visibilityState === 'hidden';

  const sorted = (): View[] => (ordered ??= [...views.values()].sort((a, b) => a.rank - b.rank || a.seq - b.seq));

  const needsFrame = (): boolean => {
    if (continuous && [...views.values()].some((v) => v.priority !== 'idle')) return true;
    for (const id of dirty) if (views.has(id)) return true;
    return false;
  };

  const schedule = (): void => {
    if (handle !== null || inFrame || hidden() || !needsFrame()) return;
    handle = { id: requestFrame(frame) };
  };

  function frame(ts: number): void {
    handle = null;
    if (hidden()) {
      lastFrame = null;
      return;
    }
    inFrame = true;
    const started = now();
    const dt = lastFrame === null ? 0 : Math.max(0, ts - lastFrame);
    lastFrame = ts;
    const toRun = sorted().filter((v) => dirty.has(v.id) || (continuous && v.priority !== 'idle'));
    for (const v of toRun) {
      if (views.get(v.id) !== v) continue; // removed by an earlier view in this frame
      if (v.priority === 'idle' && now() - started > budget) continue; // stays dirty for the next frame
      dirty.delete(v.id);
      const t0 = profiling ? now() : 0;
      try {
        v.cb(ts, dt);
      } catch (err) {
        onError(v.id, err);
      }
      if (profiling) viewMs.set(v.id, ema(viewMs.get(v.id) ?? 0, now() - t0));
    }
    if (profiling) {
      const total = now() - started;
      frameMs = ema(frameMs, total);
      worstMs = Math.max(worstMs, total);
      if (lastProfiled !== null && ts - lastProfiled < 1000) intervalMs = ema(intervalMs, ts - lastProfiled);
      lastProfiled = ts;
    }
    inFrame = false;
    if (needsFrame()) handle = { id: requestFrame(frame) };
    else lastFrame = null;
  }

  const onVisibility = (): void => {
    if (hidden()) {
      if (handle !== null) cancelFrame(handle.id);
      handle = null;
      lastFrame = null;
      return;
    }
    for (const id of views.keys()) dirty.add(id);
    schedule();
  };
  doc?.addEventListener('visibilitychange', onVisibility);

  return {
    add(id, priority, cb): Unsubscribe {
      const view: View = { id, priority, rank: FRAME_PRIORITIES.indexOf(priority), seq: seq++, cb };
      views.set(id, view);
      ordered = null;
      dirty.add(id);
      schedule();
      return () => {
        if (views.get(id) !== view) return;
        views.delete(id);
        dirty.delete(id);
        ordered = null;
      };
    },
    request(id) {
      dirty.add(id);
      schedule();
    },
    setContinuous(on) {
      if (continuous === on) return;
      continuous = on;
      if (on) schedule();
    },
    get continuous() {
      return continuous;
    },
    get scheduled() {
      return handle !== null;
    },
    ids: () => sorted().map((v) => v.id),
    setProfiling(on) {
      profiling = on;
      if (!on) {
        viewMs.clear();
        frameMs = 0;
        worstMs = 0;
        intervalMs = 0;
        lastProfiled = null;
      }
    },
    get profiling() {
      return profiling;
    },
    profile() {
      const t = now();
      const idle = lastProfiled === null || t - lastProfiled > 1000;
      const out: FrameProfile = {
        fps: idle || intervalMs <= 0 ? 0 : 1000 / intervalMs,
        frameMs,
        worstMs,
        views: sorted()
          .filter((v) => viewMs.has(v.id))
          .map((v) => ({ id: v.id, priority: v.priority, ms: viewMs.get(v.id) ?? 0 })),
      };
      worstMs = 0;
      return out;
    },
    requestAll() {
      for (const id of views.keys()) dirty.add(id);
      schedule();
    },
    dispose() {
      if (handle !== null) cancelFrame(handle.id);
      handle = null;
      views.clear();
      dirty.clear();
      ordered = null;
      doc?.removeEventListener('visibilitychange', onVisibility);
    },
  };
}

/** The app's single frame loop. */
export const frameScheduler: AppFrameScheduler = createFrameScheduler();

// Dev builds: the performance overlay (frame breakdown) when asked for with `?perf` or localStorage
// 'phiedit.dev.perf' = '1' (perfOverlay.ts).
if (import.meta.env.DEV && perfOverlayRequested()) installPerfOverlay(frameScheduler);
